<template>
  <LinkWrapper
    data-test="sub-banner-tile"
    class="relative flex flex-col w-full h-full overflow-hidden rounded shadow-md md:flex-row"
    :class="classes"
    :link-url="contentFields?.linkUrl"
    :is-external-link="contentFields?.isExternalLink"
  >
    <div v-if="bannerImage" class="relative">
      <ContentfulImage :entry="bannerImage" :width="bannerWidth" :height="bannerHeight" fit="fill" />
      <div
        v-if="showOverlay"
        data-test="sub-banner-overlay"
        class="absolute inset-0 flex flex-col justify-center h-full gap-2 px-6 text-white bg-black/40"
      >
        <CHeading v-if="contentFields.title" :level="4" :class="overlayHeadlineClasses">{{
          contentFields.title
        }}</CHeading>
        <span v-if="contentFields.textRow1" class="text-xs lg:text-sm">{{ contentFields.textRow1 }}</span>
        <span v-if="contentFields.textRow2" class="text-xs lg:text-sm">{{ contentFields.textRow2 }}</span>
      </div>
      <CHeading v-else :level="4" class="sr-only">{{ contentFields?.name }}</CHeading>
    </div>
    <div v-else class="flex flex-col h-full">
      <SubBannerLayout>
        <div class="p-5 md:p-2 lg:p-5">
          <ContentfulImage v-if="contentFields.icon" class="w-6 h-6 mr-2 lg:w-10 lg:h-10" :entry="contentFields.icon" />
          <CHeading v-if="contentFields.title" :level="4" class="text-sm font-bold leading-normal title xl:text-xl">
            {{ contentFields.title }}
          </CHeading>
          <span v-if="contentFields.textRow1" class="text-xs break-word text lg:text-sm">{{
            contentFields.textRow1
          }}</span>
          <span v-if="contentFields.textRow2" class="text-xs break-word text lg:text-sm">{{
            contentFields.textRow2
          }}</span>
        </div>

        <template #image>
          <ContentfulImage
            class="rounded z-1"
            :class="small ? 'm-1 w-12 sm:w-16 md:hidden lg:block lg:w-10 xl:w-12 2xl:w-14 2xl:m-2' : 'm-2 w-16 xl:w-24'"
            :entry="contentImage"
            :width="contentImageSize"
            :height="contentImageSize"
            fit="fill"
          />
        </template>
      </SubBannerLayout>
    </div>
  </LinkWrapper>
</template>

<script setup lang="ts">
import SubBannerLayout from './SubBannerLayout.vue';
import { BANNER_HEIGHT, BANNER_WIDTH, CONTENT_IMAGE_SIZE } from './consts';
import { getAspectRatio } from './utils';
import { setCmsTemplateClasses } from '@/config/cms';

type Props = {
  content?: any;
  small?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  content: undefined,
  small: false,
});

const contentFields = computed(() => props.content?.fields || props.content);

const bannerWidth = computed(() => BANNER_WIDTH);

const bannerHeight = computed(() => (props.small ? BANNER_HEIGHT / 2 : BANNER_HEIGHT));

const bannerImage = computed(() => contentFields.value?.image);

const contentImage = computed(() => contentFields.value?.contentImage);

const contentImageSize = computed(() => (props.small ? CONTENT_IMAGE_SIZE / 2 : CONTENT_IMAGE_SIZE));

const overlayHeadlineClasses = computed(() => ['text-sm', 'font-bold', 'md:text-2xl', 'lg:text-lg', '2xl:text-2xl']);

const hasText = computed(
  () => !!(contentFields.value?.title || contentFields.value?.textRow1 || contentFields.value?.textRow2),
);

const showOverlay = computed(() => !!(bannerImage.value && hasText.value));

const classes = computed(() => [
  setCmsTemplateClasses(contentFields.value?.template, 'sub-banner', contentFields.value?.hideStripes),
  getAspectRatio(!!bannerImage.value, props.small),
]);
</script>

<script lang="ts">
export default defineComponent({
  name: 'SubBanner',
});
</script>
